import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Monitor, Tablet as IconaTablet, X, Check, AlertTriangle, Minus, Maximize2,
} from 'lucide-react';
import api from '../../services/api';
import '../../styles/SceltaFirma.css';

/**
 * SceltaFirma — dove far firmare il paziente.
 *
 * Si chiede PRIMA di aprire qualsiasi schermata: aprire il modulo sul
 * computer per poi abbandonarlo sarebbe un passaggio sprecato.
 *
 * L'ultimo tablet usato resta selezionato, cosi' nell'uso quotidiano
 * e' un tocco solo. Se non ci sono tablet registrati la domanda non
 * compare affatto e si va dritti al computer.
 *
 * Quando si sceglie il tablet, questo componente segue anche
 * l'avanzamento: manda un consenso per volta e passa al successivo
 * quando il precedente e' firmato.
 *
 * DUE COSE DIVERSE CHE SI MANDANO ALLO STESSO TABLET
 * Con `consensi` valorizzato si manda un modello per volta, e si passa al
 * successivo quando il precedente e' firmato. Con `consensi` vuoto non
 * c'e' nessun modello: e' la pratica di un paziente importato dal vecchio
 * archivio, che deve verificare i propri dati e firmare i quattro moduli
 * in un percorso solo. Sono due rotte diverse perche' sono due cose
 * diverse; prima questo componente provava a mandare `consensi[0]` anche
 * quando l'elenco era vuoto, e il pulsante rispondeva «Cannot read
 * properties of undefined».
 *
 * SI PUO' RIDURRE A ICONA, E NON E' UN VEZZO
 * Mentre il paziente legge e firma - che sono minuti, non secondi - il
 * computer della segreteria era inutilizzabile: la finestra copriva
 * tutto e non si poteva chiudere senza interrompere la firma. Ridotta,
 * resta una barretta in basso a destra e il resto del gestionale torna
 * usabile. L'attesa continua a funzionare identica: il componente resta
 * montato, cambia solo cio' che disegna, quindi la sessione viene
 * interrogata come prima e al termine la pratica prosegue da se'.
 *
 * Props:
 *  - pazienteId, visitaId, medicoId, prestazione
 *  - consensi      elenco dei modelli da far firmare. Vuoto = pratica intera.
 *                  `_prestazione` di un modello vince su `prestazione`.
 *  - onSuPc        () => void, prosegue sul computer
 *  - onCompletato  (documenti) => void, firmati tutti sul tablet
 *  - onAnnulla     () => void
 *  - onInCorso     (vero/falso) => void, facoltativo: una firma e' in attesa
 *                  sul tablet. Chi monta la finestra dentro una schermata che
 *                  resta usabile (ridotta a icona) lo usa per non far partire
 *                  un secondo documento che qui non si manderebbe.
 *  - riapri        numero, facoltativo: quando cresce, la finestra ridotta a
 *                  icona si riapre
 */

const ULTIMO_TABLET = 'rimedichub_ultimo_tablet';
const INTERVALLO_MS = 3000;

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

const SceltaFirma = ({
    pazienteId, visitaId, medicoId, prestazione,
    consensi = [], onSuPc, onCompletato, onAnnulla, onInCorso, riapri = 0,
}) => {
    const [dispositivi, setDispositivi] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [scelto, setScelto] = useState('');
    const [errore, setErrore] = useState(null);

    // Avanzamento sul tablet
    const [inCorso, setInCorso] = useState(false);
    const [indice, setIndice] = useState(0);
    const [sessioneId, setSessioneId] = useState(null);
    const [statoSessione, setStatoSessione] = useState(null);
    const [firmati, setFirmati] = useState([]);
    const timer = useRef(null);

    useEffect(() => {
        api.get('/firma-tablet/dispositivi')
            .then((r) => {
                const elenco = r.data.data.dispositivi || [];
                setDispositivi(elenco);
                const ultimo = localStorage.getItem(ULTIMO_TABLET);
                const valido = elenco.find((d) => String(d.id) === String(ultimo));
                setScelto(valido ? String(valido.id) : (elenco[0] ? String(elenco[0].id) : ''));
            })
            .catch((e) => console.error('Dispositivi:', e))
            .finally(() => setCaricamento(false));
    }, []);

    useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

    /* ---------- invio al tablet ---------- */
    const mandaAlTablet = useCallback(async (i) => {
        try {
            setErrore(null);

            /* Nessun modello: si manda la pratica intera. */
            if (!consensi.length) {
                const r = await api.post('/firma-tablet/sessioni-pratica', {
                    paziente_id: pazienteId,
                    dispositivo_id: scelto,
                });
                setSessioneId(r.data.data.sessione.id);
                setStatoSessione('in_attesa');
                return;
            }

            const consenso = consensi[i];
            if (!consenso) {
                // Non deve capitare, ma se capita si dice cosa, non si
                // lascia esplodere una lettura su undefined.
                setErrore('Non c\'è nessun modulo da inviare.');
                setInCorso(false);
                return;
            }
            const r = await api.post('/firma-tablet/sessioni', {
                paziente_id: pazienteId,
                template_id: consenso.template_id || consenso.id,
                dispositivo_id: scelto,
                visita_id: visitaId || null,
                medico_id: medicoId || null,
                /* La prestazione del consenso, come la manda FirmaConsenso
                   alla firma a schermo (6/10/2026). Le tre strade che usano
                   questo componente - agenda, «Nuova procedura», «Genera
                   documento» della visita - la mettono in `_prestazione` di
                   ogni consenso e nessuna passa `prestazione`: il PDF firmato
                   al tablet usciva senza la riga «Prestazione:», che quello
                   firmato a schermo ha. Lo stesso documento da tutte e due le
                   parti (services/archiviaConsenso.js). */
                prestazione: consenso._prestazione || prestazione || null,
            });
            setSessioneId(r.data.data.sessione.id);
            setStatoSessione('in_attesa');
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile inviare il documento al tablet'));
            setInCorso(false);
        }
    }, [consensi, pazienteId, scelto, visitaId, medicoId, prestazione]);

    /* ---------- controllo dello stato ---------- */
    useEffect(() => {
        if (!sessioneId || !inCorso) return;

        timer.current = setInterval(async () => {
            try {
                const r = await api.get(`/firma-tablet/sessioni/${sessioneId}/stato`);
                const s = r.data.data.sessione;
                setStatoSessione(s.stato);

                if (s.stato === 'firmata') {
                    clearInterval(timer.current);
                    const nuoviFirmati = [...firmati, {
                        titolo: s.titolo, documento_id: s.documento_id
                    }];
                    setFirmati(nuoviFirmati);

                    const prossimo = indice + 1;
                    if (consensi.length && prossimo < consensi.length) {
                        setIndice(prossimo);
                        setSessioneId(null);
                        mandaAlTablet(prossimo);
                    } else {
                        setInCorso(false);
                        onCompletato(nuoviFirmati);
                    }
                } else if (s.stato === 'scaduta' || s.stato === 'annullata') {
                    clearInterval(timer.current);
                    setInCorso(false);
                    setErrore(s.stato === 'scaduta'
                        ? 'Il documento è scaduto sul tablet senza essere firmato.'
                        : 'La sessione è stata annullata.');
                }
            } catch (e) {
                console.error('Stato sessione:', e);
            }
        }, INTERVALLO_MS);

        return () => { if (timer.current) clearInterval(timer.current); };
    }, [sessioneId, inCorso, indice, consensi.length, firmati, mandaAlTablet, onCompletato]);

    const avvia = () => {
        localStorage.setItem(ULTIMO_TABLET, scelto);
        setInCorso(true);
        setIndice(0);
        setFirmati([]);
        mandaAlTablet(0);
    };

    const interrompi = async () => {
        if (timer.current) clearInterval(timer.current);
        if (sessioneId) {
            try {
                await api.post(`/firma-tablet/sessioni/${sessioneId}/annulla`,
                               { motivo: 'Interrotta dalla postazione' });
            } catch (e) { console.error('Annullamento:', e); }
        }
        setInCorso(false);
        setSessioneId(null);
    };

    /* Ridotta a icona: la finestra diventa una barretta e il resto del
       gestionale torna cliccabile. Lo stato sta qui e non in un contesto
       globale perche' vive quanto l'attesa. */
    const [ridotto, setRidotto] = useState(false);

    /* Chi ci ha montato sa se una firma e' in attesa (revisione del beta,
       6/10/2026): in «Genera documento» della visita, con la finestra
       ridotta, un secondo modello scelto cambiava l'elenco sotto un'attesa
       gia' partita e non si mandava, senza dire niente. */
    const avvisaInCorso = useRef(onInCorso);
    avvisaInCorso.current = onInCorso;
    useEffect(() => {
        if (avvisaInCorso.current) avvisaInCorso.current(inCorso);
    }, [inCorso]);
    useEffect(() => { if (riapri) setRidotto(false); }, [riapri]);

    /* Nessun tablet: la domanda non ha senso, si va sul computer */
    useEffect(() => {
        if (!caricamento && dispositivi.length === 0) onSuPc();
    }, [caricamento, dispositivi.length, onSuPc]);

    if (caricamento || dispositivi.length === 0) return null;

    /* ---------- in attesa di firma sul tablet ---------- */
    if (inCorso) {
        const nomeTablet = dispositivi.find((d) => String(d.id) === String(scelto))?.nome || 'tablet';
        const statoTesto = statoSessione === 'aperta'
            ? 'Il paziente sta leggendo il documento.'
            : 'In attesa che il paziente apra il documento.';

        /* Ridotta: nessuno sfondo che copre la pagina, solo la barretta.
           Da qui non si interrompe niente - per farlo si riapre - cosi'
           un clic distratto su una barra piccola non annulla una firma
           in corso. */
        if (ridotto) {
            return (
                <div className="scf-ridotto" role="status" aria-live="polite">
                    <IconaTablet size={16} aria-hidden="true" />
                    <div className="scf-ridotto-testo">
                        <b>{consensi[indice]?.titolo || 'Documento'}</b>
                        <span>
                            {statoTesto}
                            {consensi.length > 1 && ` — ${indice + 1} di ${consensi.length}`}
                        </span>
                    </div>
                    <div className="scf-punti piccoli"><span /><span /><span /></div>
                    <button className="scf-ridotto-btn"
                            onClick={() => setRidotto(false)}
                            title="Riapri la finestra della firma">
                        <Maximize2 size={15} aria-hidden="true" />
                    </button>
                </div>
            );
        }

        return (
            <div className="scf-sfondo">
                <div className="scf-riquadro">
                    <div className="scf-testata">
                        <span><IconaTablet size={18} /> Firma in corso su {nomeTablet}</span>
                        <button className="scf-chiudi"
                                onClick={() => setRidotto(true)}
                                title="Riduci a icona e continua a lavorare">
                            <Minus size={18} aria-hidden="true" />
                        </button>
                    </div>
                    <div className="scf-corpo">
                        <div className="scf-attesa">
                            <div className="scf-punti"><span /><span /><span /></div>
                            <p className="scf-grande">
                                {consensi[indice]?.titolo || 'Documento'}
                            </p>
                            <p className="scf-nota">
                                {statoTesto}
                            </p>
                            {consensi.length > 1 && (
                                <p className="scf-conteggio">
                                    Documento {indice + 1} di {consensi.length}
                                </p>
                            )}
                            <button className="scf-riduci"
                                    onClick={() => setRidotto(true)}>
                                <Minus size={14} aria-hidden="true" />
                                Riduci a icona e continua a lavorare
                            </button>
                        </div>

                        {firmati.length > 0 && (
                            <ul className="scf-firmati">
                                {firmati.map((f, i) => (
                                    <li key={i}><Check size={14} /> {f.titolo}</li>
                                ))}
                            </ul>
                        )}

                        {errore && (
                            <div className="scf-errore">
                                <AlertTriangle size={16} /> {errore}
                            </div>
                        )}

                        <div className="scf-azioni">
                            <button className="scf-btn" onClick={interrompi}>
                                Interrompi
                            </button>
                            <button className="scf-btn" onClick={async () => {
                                await interrompi();
                                onSuPc();
                            }}>
                                <Monitor size={16} /> Passa al computer
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ---------- scelta ---------- */
    return (
        <div className="scf-sfondo">
            <div className="scf-riquadro">
                <div className="scf-testata">
                    <span>Dove firma il paziente?</span>
                    <button className="scf-chiudi" onClick={onAnnulla}><X size={18} /></button>
                </div>
                <div className="scf-corpo">
                    {errore && (
                        <div className="scf-errore">
                            <AlertTriangle size={16} /> {errore}
                        </div>
                    )}

                    <div className="scf-scelte">
                        <button className="scf-scelta" onClick={onSuPc}>
                            <Monitor size={26} />
                            <b>Su questo computer</b>
                            <span>Firma con il mouse o sullo schermo</span>
                        </button>

                        <div className="scf-scelta scf-scelta--tablet">
                            <IconaTablet size={26} />
                            <b>Su tablet</b>
                            <select value={scelto} onChange={(e) => setScelto(e.target.value)}>
                                {dispositivi.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.nome}
                                        {parseInt(d.in_corso, 10) > 0 ? ' — occupato' : ''}
                                    </option>
                                ))}
                            </select>
                            <button className="scf-btn scf-btn--principale"
                                    onClick={avvia} disabled={!scelto}>
                                Invia al tablet
                            </button>
                        </div>
                    </div>

                    {consensi.length > 1 && (
                        <p className="scf-nota">
                            {consensi.length} documenti da firmare: sul tablet compaiono
                            uno dopo l'altro.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SceltaFirma;
